import { Location } from '@angular/common';
import { effect, HostListener, inject, Injector, Service, signal, untracked } from '@angular/core';

import { BaasI18n } from '@lvflanders/bar';
import { BarCoreService } from '@lvflanders/bar-ng';

import { BaasActionButtonOptions } from '../baas-action-button/baas-action-button.types';
import { BarDeviceType } from './bar-device-type.enum';
import { BarLayoutInternalGvkService } from './bar-layout-internal-gvk.service';
import { BarLayoutState } from './bar-layout-state.contract';
import { BarMenuState } from './bar-menu-state.type';
import { _BarBurgerprofielEngine } from './burgerprofiel/bar-burgerprofiel-engine-provider';
import { BAAS_LAYOUT_STATE } from './layout-tokens';
/** Internal BaaS layout service NOT EXPOSED */
@Service({ autoProvided: false })
export class _BaasInternalLayoutService {
    readonly #layoutState = inject<BarLayoutState>(BAAS_LAYOUT_STATE);

    readonly #injector = inject(Injector);
    readonly #location = inject(Location);
    readonly #i18n = inject(BaasI18n);
    readonly #gvk = inject(BarLayoutInternalGvkService);
    readonly #burgerprofielEngine = inject(_BarBurgerprofielEngine);
    // avoid IOC cyclic dependency error (because core service is bound to initializers)
    get #core(): BarCoreService {
        return this.#injector.get(BarCoreService);
    }

    // this will define the icon button with the home icon
    readonly internalContentActions = signal<BaasActionButtonOptions[]>([]);
    readonly deviceType = signal<BarDeviceType | undefined>(undefined);

    readonly #mainAction = signal<BaasActionButtonOptions>(undefined);
    readonly mainAction = this.#mainAction.asReadonly();

    readonly #backAction = signal<BaasActionButtonOptions>(undefined);
    readonly backAction = this.#backAction.asReadonly();

    // TODO in responsive modus this should be a computed based on device type?
    readonly #smallDevice = signal<boolean>(undefined);
    readonly smallDevice = this.#smallDevice.asReadonly();

    readonly box = !this.#burgerprofielEngine.visible;

    constructor() {
        effect(() => {
            // NOTE whenever devicetype is altered we need to recompute if it concerns a small device
            this.deviceType();
            untracked(() => this.#defineSmallDevice());
        });
    }
    @HostListener('window:resize', ['$event'])
    onResize(event) {
        this.#defineSmallDevice();
    }

    setMenuState(state: BarMenuState): void {
        document.documentElement.setAttribute('data-bar-menu-state', state);
        // gvk will give us the menu state
        this.#gvk.persistMenuState(state);
    }

    init(): void {
        this.#backAction.set({
            action: () => {
                this.#location.back();
            },
            tooltip: this.#i18n.get('DOMAIN_BAR_LAYOUT.BACK'),
            icon: {
                class: 'fas fa-arrow-left',
                srText: this.#i18n.get('DOMAIN_BAR_LAYOUT.BACK'),
                iconOnly: true
            }
        });
        const mainAction = {
            tooltip: this.#i18n.get('DOMAIN_BAR_LAYOUT.MAINSCREEN'),
            icon: {
                class: 'fas fa-home',
                srText: this.#i18n.get('DOMAIN_BAR_LAYOUT.MAINSCREEN'),
                iconOnly: true
            }
        } as BaasActionButtonOptions;

        if (this.#layoutState.homeUrl != null) {
            // use provides an override for the layout module use this (which is href for now)
            mainAction.href = this.#layoutState.homeUrl;
        } else {
            mainAction.action = () => {
                // just use default core functionality if no override
                this.#core.navigateHome();
            };
        }

        this.#mainAction.set(mainAction);

        this.#defineSmallDevice();
    }

    #defineSmallDevice(): void {
        this.#smallDevice.set(window.innerWidth < 1200);
    }
}
